import React, { useState, useRef, useEffect } from 'react';
import {
  UtensilsCrossed, ChevronRight,
  MapPin, Phone, Mail, Instagram, Music, Sun,
  ExternalLink, ListMusic, Camera,
  CalendarDays, Clock, Info, Youtube, ChevronLeft
} from 'lucide-react';

/* ─────────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────────── */
const translations = {
  tr: {
    digitalMenu: "Dijital Menü",
    menuTab: "Menü",
    entertainmentTab: "Canlı Müzik",
    enjoy: "Afiyet olsun!",
    ingredients: "İçerik:",
    location: "Konum",
    contact: "İletişim",
    noEvent: "Şu an aktif etkinlik yok",
    noEventSub: "Yakında harika konuklar için takipte kalın!",
    nextEvent: "Sonraki Etkinlik",
    setlist: "Repertuar / Setlist",
    gallery: "Fotoğraf Galerisi",
    watchVideo: "Videoyu İzle",
    listenSpotify: "Spotify'da Dinle",
    ticket: "Giriş Bileti",
    ticketPrice: "Katılım Bedeli",
    items: "ürün",
    otherCategories: "Diğer Kategoriler",
  },
  en: {
    digitalMenu: "Digital Menu",
    menuTab: "Menu",
    entertainmentTab: "Live Music",
    enjoy: "Enjoy your meal!",
    ingredients: "Ingredients:",
    location: "Location",
    contact: "Contact",
    noEvent: "No active event right now",
    noEventSub: "Stay tuned for amazing upcoming guests!",
    nextEvent: "Next Event",
    setlist: "Setlist / Repertoire",
    gallery: "Photo Gallery",
    watchVideo: "Watch Video",
    listenSpotify: "Listen on Spotify",
    ticket: "Entry Ticket",
    ticketPrice: "Participation Fee",
    items: "items",
    otherCategories: "Other Categories",
  }
};

/* ─────────────────────────────────────────────
   MENU CATEGORIES
───────────────────────────────────────────── */
const menuCategories = [
  /* ── KAMPANYALAR ── */
  {
    id: 'kampanyalar',
    label: { tr: 'Kampanyalar', en: 'Deals' },
    emoji: '🏷️',
    color: '#FF6B4A',
    items: [
      { name: { tr: "Cookie + Kahve", en: "Cookie + Coffee" }, price: "300 TL", badge: { tr: "🔥 Fırsat", en: "🔥 Deal" }, description: { tr: "Çikolatalı cookie ile istediğiniz herhangi bir kahve kombinasyonu.", en: "Chocolate cookie with any coffee of your choice." }, ingredients: { tr: "Çikolatalı cookie, seçtiğiniz kahve", en: "Chocolate cookie, coffee of your choice" }, image: "/images/food/cookie_kahve.png" },
      { name: { tr: "Sandviç + Çay", en: "Sandwich + Tea" }, price: "250 TL", badge: { tr: "🔥 Fırsat", en: "🔥 Deal" }, description: { tr: "İstediğiniz sandviç çeşidi ile sıcacık çay ikramı.", en: "Any sandwich with hot tea." }, ingredients: { tr: "Seçtiğiniz sandviç, çay", en: "Sandwich of your choice, tea" }, image: "/images/food/sandvic_cay.png" },
      { name: { tr: "Sooho Sepeti + İçecek", en: "Sooho Basket + Drink" }, price: "100 TL/kutu", badge: { tr: "⚡ Süper Fiyat", en: "⚡ Super Price" }, description: { tr: "Sooho Sepeti yanına içilen her kutu içecek sadece 100 TL!", en: "Every canned drink with Sooho Basket only 100 TL!" }, ingredients: { tr: "Sooho Sepeti + kutu içecek", en: "Sooho Basket + canned drink" }, image: "/images/food/sooho_sepeti.png" },
    ]
  },
  /* ── SICAK KAHVE ── */
  {
    id: 'sicak-kahve',
    label: { tr: 'Sıcak Kahve', en: 'Hot Coffee' },
    emoji: '☕',
    color: '#92400e',
    items: [
      { name: { tr: "Espresso", en: "Espresso" }, price: "180 TL", description: { tr: "Yoğun ve aromatik, tek shot espresso deneyimi.", en: "Intense and aromatic single shot espresso." }, ingredients: { tr: "Premium espresso çekirdeği", en: "Premium espresso beans" }, image: "/images/drinks/espresso.png" },
      { name: { tr: "Americano", en: "Americano" }, price: "200 TL", description: { tr: "Espresso ve sıcak su ile hazırlanan yumuşak kahve.", en: "Smooth coffee with espresso and hot water." }, ingredients: { tr: "Espresso, sıcak su", en: "Espresso, hot water" }, image: "/images/drinks/americano.png" },
      { name: { tr: "Latte", en: "Latte" }, price: "200 TL", description: { tr: "Kadifemsi süt köpüğüyle yumuşatılmış yoğun espresso.", en: "Intense espresso softened with velvety milk foam." }, ingredients: { tr: "Espresso, buharda ısıtılmış süt, süt köpüğü", en: "Espresso, steamed milk, milk foam" }, image: "/images/drinks/latte.png" },
      { name: { tr: "Karamel Latte", en: "Caramel Latte" }, price: "230 TL", description: { tr: "Karamel şurubuyla tatlandırılmış kremsi latte.", en: "Creamy latte flavored with caramel syrup." }, ingredients: { tr: "Espresso, süt, karamel şurubu, süt köpüğü", en: "Espresso, milk, caramel syrup, milk foam" }, image: "/images/drinks/caramel_latte.png" },
      { name: { tr: "Cappuccino", en: "Cappuccino" }, price: "200 TL", description: { tr: "Espresso, süt ve yoğun süt köpüğünün mükemmel dengesi.", en: "Perfect balance of espresso, milk and dense milk foam." }, ingredients: { tr: "Espresso, süt, yoğun süt köpüğü", en: "Espresso, milk, dense milk foam" }, image: "/images/drinks/cappuccino.png" },
      { name: { tr: "Mocha", en: "Mocha" }, price: "230 TL", description: { tr: "Espresso, çikolata ve süt köpüğünün tatlı buluşması.", en: "Sweet meeting of espresso, chocolate and milk foam." }, ingredients: { tr: "Espresso, çikolata sosu, süt, süt köpüğü", en: "Espresso, chocolate sauce, milk, milk foam" }, image: "/images/drinks/mocha.png" },
      { name: { tr: "Flat White", en: "Flat White" }, price: "200 TL", description: { tr: "Mikro köpüklü süt ve espressonun kadifemsi uyumu.", en: "Velvety harmony of microfoam milk and espresso." }, ingredients: { tr: "Espresso, mikro köpüklü süt", en: "Espresso, microfoam milk" }, image: "/images/drinks/flat_white.png" },
      { name: { tr: "Cortado", en: "Cortado" }, price: "200 TL", description: { tr: "Espresso ve eşit miktarda ılık sütle dengelenmiş.", en: "Espresso balanced with equal amounts of warm milk." }, ingredients: { tr: "Espresso, ılık süt", en: "Espresso, warm milk" }, image: "/images/drinks/cortado.png" },
      { name: { tr: "Macchiato", en: "Macchiato" }, price: "200 TL", description: { tr: "Espresso üzerine bir damla süt köpüğü ile.", en: "Espresso with a drop of milk foam." }, ingredients: { tr: "Espresso, süt köpüğü", en: "Espresso, milk foam" }, image: "/images/drinks/macchiato.png" },
      { name: { tr: "Filtre Kahve", en: "Filter Coffee" }, price: "200 TL", description: { tr: "Özenle demlenen, aromatik filtre kahve.", en: "Carefully brewed aromatic filter coffee." }, ingredients: { tr: "Filtre kahve çekirdeği", en: "Filter coffee beans" }, image: "/images/drinks/filter_coffee.png" },
    ]
  },
  /* ── TÜRK KAHVESİ ── */
  {
    id: 'turk-kahvesi',
    label: { tr: 'Türk Kahvesi', en: 'Turkish Coffee' },
    emoji: '🫘',
    color: '#6b3a1f',
    items: [
      { name: { tr: "Türk Kahvesi", en: "Turkish Coffee" }, price: "200 TL", description: { tr: "Geleneksel yöntemle pişirilmiş, köpüklü Türk kahvesi.", en: "Traditional brewed frothy Turkish coffee." }, ingredients: { tr: "Türk kahvesi", en: "Turkish coffee" }, image: "/images/drinks/turkish_coffee.png" },
      { name: { tr: "Double Türk Kahvesi", en: "Double Turkish Coffee" }, price: "250 TL", description: { tr: "Duble fincanda ekstra keyifli Türk kahvesi.", en: "Extra enjoyable Turkish coffee in a double cup." }, ingredients: { tr: "Çift doz Türk kahvesi", en: "Double dose Turkish coffee" }, image: "/images/drinks/double_turkish_coffee.png" },
      { name: { tr: "Dibek Kahvesi", en: "Dibek Coffee" }, price: "200 TL", description: { tr: "Taş dibekte öğütülmüş, aromatik ve geleneksel kahve.", en: "Aromatic coffee ground in a stone mortar." }, ingredients: { tr: "Dibek kahvesi", en: "Dibek coffee" }, image: "/images/drinks/dibek_coffee.png" },
      { name: { tr: "Menengiç Kahvesi", en: "Menengiç Coffee" }, price: "220 TL", description: { tr: "Kafeinsiz, doğal terebinth meyvesinden kahve.", en: "Caffeine-free natural terebinth fruit coffee." }, ingredients: { tr: "Menengiç çekirdeği", en: "Menengiç beans" }, image: "/images/drinks/menengi_coffee.png" },
      { name: { tr: "Damla Sakızlı Türk Kahvesi", en: "Mastic Turkish Coffee" }, price: "220 TL", description: { tr: "Damla sakızının eşsiz aromasıyla zenginleştirilmiş Türk kahvesi.", en: "Turkish coffee enriched with unique mastic aroma." }, ingredients: { tr: "Türk kahvesi, damla sakızı", en: "Turkish coffee, mastic" }, image: "/images/drinks/mastic_turkish_coffee.png" },
    ]
  },
  /* ── ÇAY & SICAK ── */
  {
    id: 'cay-sicak',
    label: { tr: 'Çay & Sıcak', en: 'Tea & Hot' },
    emoji: '🫖',
    color: '#059669',
    items: [
      { name: { tr: "Çay", en: "Tea" }, price: "50 TL", description: { tr: "Geleneksel demleme Türk çayı, sıcacık ve ferahlatıcı.", en: "Traditional brewed Turkish tea, warm and refreshing." }, ingredients: { tr: "Siyah çay yaprakları", en: "Black tea leaves" }, image: "/images/drinks/tea.png" },
      { name: { tr: "Fincan Çay", en: "Cup of Tea" }, price: "100 TL", description: { tr: "Fincan servisinde özel demleme çay.", en: "Specially brewed tea served in a cup." }, ingredients: { tr: "Siyah çay yaprakları", en: "Black tea leaves" }, image: "/images/drinks/cup_of_tea.png" },
      { name: { tr: "Bitki Çayı", en: "Herbal Tea" }, price: "200 TL", description: { tr: "Doğal bitki karışımından hazırlanan rahatlatıcı çay.", en: "Relaxing tea from natural herbal mixture." }, ingredients: { tr: "Çeşitli bitki karışımı", en: "Various herbal mixtures" }, image: "/images/drinks/herbal_tea.png" },
      { name: { tr: "Sahlep", en: "Sahlep" }, price: "220 TL", description: { tr: "Tarçın serpilmiş, sıcacık geleneksel içecek.", en: "Warm traditional drink with cinnamon." }, ingredients: { tr: "Sahlep tozu, süt, şeker, tarçın", en: "Sahlep powder, milk, sugar, cinnamon" }, image: "/images/drinks/sahlep.png" },
      { name: { tr: "Sıcak Çikolata", en: "Hot Chocolate" }, price: "220 TL", description: { tr: "Yoğun çikolata lezzetiyle ısıtan kremsi içecek.", en: "Creamy drink with intense chocolate flavor." }, ingredients: { tr: "Bitter çikolata, süt, şeker", en: "Dark chocolate, milk, sugar" }, image: "/images/drinks/hot_chocolate.png" },
      { name: { tr: "Beyaz Sıcak Çikolata", en: "White Hot Chocolate" }, price: "220 TL", description: { tr: "Beyaz çikolatanın tatlı ve kremsi sıcaklığı.", en: "The sweet and creamy warmth of white chocolate." }, ingredients: { tr: "Beyaz çikolata, süt, şeker", en: "White chocolate, milk, sugar" }, image: "/images/drinks/white_hot_chocolate.png" },
    ]
  },
  /* ── SOĞUK KAHVE (YENİ) ── */
  {
    id: 'soguk-kahve',
    label: { tr: 'Soğuk Kahve', en: 'Cold Coffee' },
    emoji: '🧋',
    color: '#0369a1',
    items: [
      { name: { tr: 'Ice Americano', en: 'Ice Americano' }, price: '200 TL', description: { tr: 'Espresso ve buz ile hazırlanan serinletici soğuk Americano.', en: 'Refreshing iced Americano with espresso and ice.' }, ingredients: { tr: 'Espresso, buz, su', en: 'Espresso, ice, water' }, image: "/images/drinks/ice_americano.png" },
      { name: { tr: 'Ice Latte', en: 'Ice Latte' }, price: '200 TL', description: { tr: 'Soğuk süt ve espresso ile hazırlanan tatlı buzlu latte.', en: 'Sweet iced latte made with cold milk and espresso.' }, ingredients: { tr: 'Espresso, soğuk süt, buz', en: 'Espresso, cold milk, ice' }, image: "/images/drinks/ice_latte.png" },
      { name: { tr: 'Ice Caramel Latte', en: 'Ice Caramel Latte' }, price: '230 TL', description: { tr: 'Karamel şurubu ve buzlu süt ile tatlandırılmış soğuk latte.', en: 'Iced latte sweetened with caramel syrup and cold milk.' }, ingredients: { tr: 'Espresso, soğuk süt, karamel şurubu, buz', en: 'Espresso, cold milk, caramel syrup, ice' }, image: "/images/drinks/ice_caramel_latte.png" },
      { name: { tr: 'Ice White Chocolate Mocha', en: 'Ice White Choc Mocha' }, price: '230 TL', description: { tr: 'Beyaz çikolata sosuyla tatlandırılmış buzlu mocha.', en: 'Iced mocha sweetened with white chocolate sauce.' }, ingredients: { tr: 'Espresso, beyaz çikolata sosu, soğuk süt, buz', en: 'Espresso, white chocolate sauce, cold milk, ice' }, image: "/images/drinks/ice_white_choc_mocha.png" },
      { name: { tr: 'Ice Mocha', en: 'Ice Mocha' }, price: '230 TL', description: { tr: 'Bitter çikolata sosuyla zenginleştirilmiş soğuk mocha.', en: 'Cold mocha enriched with dark chocolate sauce.' }, ingredients: { tr: 'Espresso, çikolata sosu, soğuk süt, buz', en: 'Espresso, chocolate sauce, cold milk, ice' }, image: "/images/drinks/ice_mocha.png" },
      { name: { tr: 'Affogato', en: 'Affogato' }, price: '230 TL', description: { tr: 'Sıcak espresso ile soğuk vanilyalı dondurmanın buluşması.', en: 'Hot espresso meets cold vanilla ice cream.' }, ingredients: { tr: 'Espresso, vanilyalı dondurma', en: 'Espresso, vanilla ice cream' }, image: "/images/drinks/affogato.png" },
    ]
  },
  /* ── SOĞUK İÇECEK ── */
  {
    id: 'soguk-icecek',
    label: { tr: 'Soğuk İçecek', en: 'Cold Drinks' },
    emoji: '🧊',
    color: '#0284c7',
    items: [
      { name: { tr: "Kola / Fanta / Sprite / Fuse Tea", en: "Coke / Fanta / Sprite / Fuse Tea" }, price: "200 TL", description: { tr: "Serinleten klasik kutu gazlı içecekler ve soğuk çay.", en: "Refreshing classic canned soft drinks and ice tea." }, ingredients: { tr: "Kutu gazlı içecek veya soğuk çay", en: "Canned soft drink or ice tea" }, image: "/images/drinks/coke_fanta_sprite_fuse_tea.png" },
      { name: { tr: "Meyve Suları", en: "Fruit Juices" }, price: "200 TL", description: { tr: "Elma, Şeftali, Karışık, Vişne çeşitleri.", en: "Apple, Peach, Mixed, Cherry varieties." }, ingredients: { tr: "Seçtiğiniz meyve suyu", en: "Fruit juice of your choice" }, image: "/images/drinks/fruit_juices.png" },
      { name: { tr: "Redbull", en: "Redbull" }, price: "200 TL", description: { tr: "Enerji veren klasik enerji içeceği.", en: "Classic energy drink." }, ingredients: { tr: "Redbull enerji içeceği", en: "Redbull energy drink" }, image: "/images/drinks/redbull.png" },
      { name: { tr: "Aromalı Redbull", en: "Flavored Redbull" }, price: "250 TL", description: { tr: "Çeşitli aromalarda enerji dolu serinlik.", en: "Energy-filled coolness in various flavors." }, ingredients: { tr: "Aromalı Redbull", en: "Flavored Redbull" }, image: "/images/drinks/flavored_redbull.png" },
      { name: { tr: "Maden Suyu", en: "Mineral Water" }, price: "100 TL", description: { tr: "Ferahlatıcı doğal maden suyu.", en: "Refreshing natural mineral water." }, ingredients: { tr: "Maden suyu", en: "Mineral water" }, image: "/images/drinks/mineral_water.png" },
      { name: { tr: "Meyveli Soda", en: "Fruit Soda" }, price: "120 TL", description: { tr: "Çeşitli meyve aromalı serinletici soda.", en: "Refreshing soda with various fruit flavors." }, ingredients: { tr: "Soda, meyve aroması", en: "Soda, fruit flavor" }, image: "/images/drinks/fruit_soda.png" },
      { name: { tr: "Churchill", en: "Churchill" }, price: "150 TL", description: { tr: "Premium gazlı içecek deneyimi.", en: "Premium soft drink experience." }, ingredients: { tr: "Churchill gazlı içecek", en: "Churchill soft drink" }, image: "/images/drinks/churchill.png" },
      { name: { tr: "Ayran", en: "Ayran" }, price: "100 TL", description: { tr: "Saf Türk yoğurt içeceği. Serinletici.", en: "Pure Turkish yogurt drink. Refreshing." }, ingredients: { tr: "Yoğurt, su, tuz", en: "Yogurt, water, salt" }, image: "/images/drinks/ayran.png" },
      { name: { tr: "Su", en: "Water" }, price: "60 TL", description: { tr: "Saf ve ferahlatıcı içme suyu.", en: "Pure and refreshing drinking water." }, ingredients: { tr: "İçme suyu", en: "Drinking water" }, image: "/images/drinks/water.png" },
    ]
  },
  /* ── FROZENLAR (YENİ) ── */
  {
    id: 'frozenlar',
    label: { tr: 'Frozenlar', en: 'Frozens' },
    emoji: '🫧',
    color: '#0891b2',
    items: [
      { name: { tr: 'Çilek Frozen', en: 'Strawberry Frozen' }, price: '220 TL', description: { tr: 'Taze çilek püresiyle hazırlanan buzlu, yoğun aromalı frozen.', en: 'Iced frozen made with fresh strawberry puree.' }, ingredients: { tr: 'Çilek püresi, buz, şeker, süt', en: 'Strawberry puree, ice, sugar, milk' }, image: "/images/drinks/cilek_frozen.png" },
      { name: { tr: 'Mango Frozen', en: 'Mango Frozen' }, price: '220 TL', description: { tr: 'Egzotik mango ile hazırlanan serinletici buzlu içecek.', en: 'Refreshing iced drink made with exotic mango.' }, ingredients: { tr: 'Mango püresi, buz, şeker, süt', en: 'Mango puree, ice, sugar, milk' }, image: "/images/drinks/mango_frozen.png" },
      { name: { tr: 'Böğürtlen Frozen', en: 'Blackberry Frozen' }, price: '220 TL', description: { tr: 'Taze böğürtlen aromasıyla dolu, ekşimsi-tatlı frozen.', en: 'Sweet-tart frozen full of fresh blackberry flavor.' }, ingredients: { tr: 'Böğürtlen püresi, buz, şeker, süt', en: 'Blackberry puree, ice, sugar, milk' }, image: "/images/drinks/blackberry_frozen.png" },
      { name: { tr: 'Orman Meyveli Frozen', en: 'Forest Berries Frozen' }, price: '220 TL', description: { tr: 'Karışık orman meyvelerinin muhteşem buluşması.', en: 'A magnificent mix of forest berries.' }, ingredients: { tr: 'Karışık orman meyveleri, buz, şeker, süt', en: 'Mixed forest berries, ice, sugar, milk' }, image: "/images/drinks/forest_berries_frozen.png" },
      { name: { tr: 'Kavun - Lime Frozen', en: 'Melon - Lime Frozen' }, price: '220 TL', description: { tr: 'Kavun tatlılığı ile limonun ferahlatıcı asidinin dengesi.', en: 'Balance of melon sweetness and refreshing lime acidity.' }, ingredients: { tr: 'Kavun püresi, lime suyu, buz, şeker', en: 'Melon puree, lime juice, ice, sugar' }, image: "/images/drinks/melon_lime_frozen.png" },
      { name: { tr: 'Limon Frozen', en: 'Lemon Frozen' }, price: '220 TL', description: { tr: 'Ekşi ve serinletici, klasik limon frozen.', en: 'Tangy and refreshing classic lemon frozen.' }, ingredients: { tr: 'Limon suyu, buz, şeker, su', en: 'Lemon juice, ice, sugar, water' }, image: "/images/drinks/lemon_frozen.png" },
    ]
  },
  /* ── LİMONATA (YENİ) ── */
  {
    id: 'limonata',
    label: { tr: 'Limonata', en: 'Lemonade' },
    emoji: '🍋',
    color: '#ca8a04',
    items: [
      { name: { tr: 'Klasik Limonata', en: 'Classic Lemonade' }, price: '200 TL', description: { tr: 'Taze sıkılmış limon, şeker ve soğuk su ile hazırlanan ferahlatıcı içecek.', en: 'Refreshing drink with freshly squeezed lemon, sugar and cold water.' }, ingredients: { tr: 'Taze limon, şeker, soğuk su, buz', en: 'Fresh lemon, sugar, cold water, ice' }, image: "/images/drinks/classic_lemonade.png" },
      { name: { tr: 'Naneli Limonata', en: 'Mint Lemonade' }, price: '220 TL', description: { tr: 'Taze nane yapraklarıyla zenginleştirilmiş serinletici limonata.', en: 'Refreshing lemonade enriched with fresh mint leaves.' }, ingredients: { tr: 'Taze limon, taze nane, şeker, soğuk su, buz', en: 'Fresh lemon, fresh mint, sugar, cold water, ice' }, image: "/images/drinks/mint_lemonade.png" },
      { name: { tr: 'Çilekli Limonata', en: 'Strawberry Lemonade' }, price: '220 TL', description: { tr: 'Taze çilek püresi ile hazırlanan pembe renkli limonata.', en: 'Pink colored lemonade made with fresh strawberry puree.' }, ingredients: { tr: 'Taze limon, çilek püresi, şeker, soğuk su, buz', en: 'Fresh lemon, strawberry puree, sugar, cold water, ice' }, image: "/images/drinks/strawberry_lemonade.png" },
      { name: { tr: 'Çiçekli Limonata', en: 'Floral Lemonade' }, price: '220 TL', description: { tr: 'Hibiskus çiçeğiyle renklendirilen, çiçeksi aromalı limonata.', en: 'Lemonade colored with hibiscus flower and floral aroma.' }, ingredients: { tr: 'Taze limon, hibiskus çiçeği, şeker, soğuk su, buz', en: 'Fresh lemon, hibiscus flower, sugar, cold water, ice' }, image: "/images/drinks/floral_lemonade.png" },
    ]
  },
  /* ── MİLKSHAKE (YENİ) ── */
  {
    id: 'milkshake',
    label: { tr: 'Milkshake', en: 'Milkshake' },
    emoji: '🥤',
    color: '#9333ea',
    items: [
      { name: { tr: 'Çilekli Milkshake', en: 'Strawberry Milkshake' }, price: '230 TL', description: { tr: 'Dondurma ve taze çilek ile hazırlanan kremsi milkshake.', en: 'Creamy milkshake with ice cream and fresh strawberries.' }, ingredients: { tr: 'Vanilyalı dondurma, çilek, süt', en: 'Vanilla ice cream, strawberry, milk' }, image: "/images/drinks/strawberry_milkshake.png" },
      { name: { tr: 'Çikolatalı Milkshake', en: 'Chocolate Milkshake' }, price: '230 TL', description: { tr: 'Bitter çikolata ile zenginleştirilmiş yoğun milkshake.', en: 'Rich milkshake enriched with dark chocolate.' }, ingredients: { tr: 'Çikolatalı dondurma, bitter çikolata sosu, süt', en: 'Chocolate ice cream, dark chocolate sauce, milk' }, image: "/images/drinks/chocolate_milkshake.png" },
      { name: { tr: 'Karamelli Milkshake', en: 'Caramel Milkshake' }, price: '230 TL', description: { tr: 'Karamel sosu ve vanilyalı dondurma ile hazırlanan tatlı milkshake.', en: 'Sweet milkshake with caramel sauce and vanilla ice cream.' }, ingredients: { tr: 'Vanilyalı dondurma, karamel sosu, süt', en: 'Vanilla ice cream, caramel sauce, milk' }, image: "/images/drinks/caramel_milkshake.png" },
      { name: { tr: 'Vanilyalı Milkshake', en: 'Vanilla Milkshake' }, price: '230 TL', description: { tr: 'Saf vanilyalı dondurma ile hazırlanan klasik milkshake.', en: 'Classic milkshake with pure vanilla ice cream.' }, ingredients: { tr: 'Vanilyalı dondurma, süt, vanilya özütü', en: 'Vanilla ice cream, milk, vanilla extract' }, image: "/images/drinks/vanilla_milkshake.png" },
    ]
  },
  /* ── KOKTEYLER (YENİ) ── */
  {
    id: 'kokteyler',
    label: { tr: 'Kokteyler', en: 'Cocktails' },
    emoji: '🍹',
    color: '#be185d',
    items: [
      { name: { tr: 'Kuzu Kulağı Kokteyl', en: 'Sorrel Cocktail' }, price: '250 TL', description: { tr: 'Kuzu kulağı bitkisiyle hazırlanan doğal, ekşimsi-tatlı kokteyl.', en: 'Natural sweet-sour cocktail made with sorrel herb.' }, ingredients: { tr: 'Kuzu kulağı, limon, şeker, soda, buz', en: 'Sorrel, lemon, sugar, soda, ice' }, image: "/images/drinks/sorrel_cocktail.png" },
      { name: { tr: 'Hibiscus Berry Kokteyl', en: 'Hibiscus Berry Cocktail' }, price: '250 TL', description: { tr: 'Hibiskus çiçeği ve orman meyvelerinden hazırlanan mor renkte kokteyl.', en: 'Purple cocktail made from hibiscus flower and forest berries.' }, ingredients: { tr: 'Hibiskus, orman meyveleri, limon, soda, buz', en: 'Hibiscus, forest berries, lemon, soda, ice' }, image: "/images/drinks/hibiscus_berry_cocktail.png" },
      { name: { tr: 'Redbull Kokteyl', en: 'Redbull Cocktail' }, price: '280 TL', description: { tr: 'Red Bull ile özel meyveli karışımdan oluşan enerjik kokteyl.', en: 'Energetic cocktail with Red Bull and special fruity mix.' }, ingredients: { tr: 'Red Bull, meyve şurubu, buz', en: 'Red Bull, fruit syrup, ice' }, image: "/images/drinks/redbull_cocktail.png" },
      { name: { tr: 'Mojito', en: 'Mojito' }, price: '250 TL', description: { tr: 'Taze nane, limon, şeker ve sodayla hazırlanan klasik mojito.', en: 'Classic mojito with fresh mint, lemon, sugar and soda.' }, ingredients: { tr: 'Taze nane, limon, şeker, soda, buz', en: 'Fresh mint, lemon, sugar, soda, ice' }, image: "/images/drinks/mojito.png" },
    ]
  },
  /* ── TOSTLAR ── */
  {
    id: 'tostlar',
    label: { tr: 'Kahvaltı & Tostlar', en: 'Breakfast & Toasts' },
    emoji: '🥪',
    color: '#b45309',
    items: [
      { name: { tr: "Kahvaltı", en: "Breakfast" }, price: "450 TL", description: { tr: "2 çeşit peynir, 2 çeşit zeytin, bal, tereyağ, yumurta, domates, salatalık, biber, sigara böreği.", en: "Rich variety breakfast platter." }, ingredients: { tr: "Peynir, zeytin, bal, tereyağ, yumurta, börek", en: "Cheese, olives, honey, butter, egg, pastry" }, image: "/images/food/kar_tostu.png" },
      { name: { tr: "Brunch", en: "Brunch" }, price: "550 TL", description: { tr: "Zengin içerikli brunch tabağı — taze ürünler ve özel katkılarla hazırlanan sabah ziyafeti.", en: "Rich brunch plate with fresh ingredients and special additions." }, ingredients: { tr: "Çeşitli taze malzemeler, özel brunch içerikleri", en: "Various fresh ingredients, special brunch items" }, image: "/images/food/akdeniz_tostu.png" },

      { name: { tr: "Kar Tostu", en: "Snow Toast" }, price: "270 TL", description: { tr: "Kar kaşar peyniri ile servis edilen, hafif ve lezzetli klasik tost.", en: "Classic toast with kashar cheese." }, ingredients: { tr: "Taze ekmek, kaşar peyniri, tereyağı", en: "Fresh bread, kashar cheese, butter" }, image: "/images/food/kar_tostu.png" },
      { name: { tr: "Akdeniz Tostu", en: "Mediterranean Toast" }, price: "270 TL", description: { tr: "Beyaz peynir ve domatesin enfes lezzeti, Akdeniz esintisiyle.", en: "Feta cheese and tomatoes with a Mediterranean breeze." }, ingredients: { tr: "Taze ekmek, beyaz peynir, domates, zeytinyağı", en: "Fresh bread, feta cheese, tomato, olive oil" }, image: "/images/food/akdeniz_tostu.png" },
      { name: { tr: "Dağ Tostu", en: "Mountain Toast" }, price: "320 TL", description: { tr: "Sucuk ve kaşarın nefis birlikteliği ile doyurucu seçim.", en: "Satisfying choice with Turkish sausage and kashar." }, ingredients: { tr: "Taze ekmek, sucuk, kaşar peyniri, tereyağı", en: "Fresh bread, Turkish sausage, kashar cheese, butter" }, image: "/images/food/dag_tostu.png" },
    ]
  },
  /* ── BURGERLER ── */
  {
    id: 'burgerler',
    label: { tr: 'Burgerler', en: 'Burgers' },
    emoji: '🍔',
    color: '#dc2626',
    items: [
      { name: { tr: "Hamburger", en: "Hamburger" }, price: "400 TL", description: { tr: "180 gr dana köfte, marul, turşu, domates ve özel sosumuzla.", en: "180 gr beef patty, lettuce, pickles, tomatoes and our special sauce." }, ingredients: { tr: "Dana köfte, hamburger ekmeği, marul, turşu, domates, özel sos", en: "Beef patty, burger bun, lettuce, pickles, tomato, special sauce" }, image: "/images/food/hamburger.png" },
      { name: { tr: "Cheese Burger", en: "Cheese Burger" }, price: "420 TL", description: { tr: "180 gr dana köfte, cheddar peyniri ve özel sos.", en: "180 gr beef patty, cheddar cheese and special sauce." }, ingredients: { tr: "Dana köfte, cheddar peyniri, hamburger ekmeği, marul, turşu, domates", en: "Beef patty, cheddar cheese, burger bun, lettuce, pickles, tomato" }, image: "/images/food/cheese_burger.png" },
      { name: { tr: "Dağ Burger", en: "Mountain Burger" }, price: "450 TL", description: { tr: "180 gr dana köfte, cheddar peyniri, közlenmiş biber.", en: "180 gr beef patty, cheddar cheese, roasted peppers." }, ingredients: { tr: "Dana köfte, cheddar peyniri, közlenmiş biber, özel sos", en: "Beef patty, cheddar cheese, roasted pepper, special sauce" }, image: "/images/food/dag_burger.png" },
      { name: { tr: "SOO HO Burger", en: "SOO HO Burger" }, price: "450 TL", badge: { tr: "⭐ Şef Önerisi", en: "⭐ Chef's Pick" }, description: { tr: "180 gr dana köfte, cheddar, halloumi peyniri, karamelize soğan.", en: "180 gr beef patty, cheddar, halloumi cheese, caramelized onions." }, ingredients: { tr: "Dana köfte, cheddar, halloumi, karamelize soğan, özel sos", en: "Beef patty, cheddar, halloumi, caramelized onion, special sauce" }, image: "/images/food/sooho_burger.png" },
      { name: { tr: "Sucuk Ekmek", en: "Sausage Sandwich" }, price: "400 TL", description: { tr: "Sucuk ve ekmek, isteğe göre domates, marul, turşu.", en: "Sausage and bread; tomato, lettuce, pickles optional." }, ingredients: { tr: "Sucuk, taze ekmek, opsiyonel: domates, marul, turşu", en: "Turkish sausage, fresh bread, optional: tomato, lettuce, pickles" }, image: "/images/food/sucuk_ekmek.png" },
      { name: { tr: "Köfte Ekmek", en: "Meatball Sandwich" }, price: "400 TL", description: { tr: "Köfte ve ekmek, isteğe göre domates, marul, turşu.", en: "Meatballs and bread; tomato, lettuce, pickles optional." }, ingredients: { tr: "Köfte, taze ekmek, opsiyonel: domates, marul, turşu", en: "Meatballs, fresh bread, optional: tomato, lettuce, pickles" }, image: "/images/food/kofte_ekmek.png" },
    ]
  },
  /* ── ATIŞTIRMALIK ── */
  {
    id: 'atistirmalik',
    label: { tr: 'Atıştırmalık', en: 'Snacks' },
    emoji: '🍟',
    color: '#d97706',
    items: [
      { name: { tr: "Patates Sepeti", en: "French Fries Basket" }, price: "200 TL", description: { tr: "Altın sarısı kızarmış, çıtır çıtır patates dilimleri.", en: "Golden fried, crispy potato slices." }, ingredients: { tr: "Taze patates, tuz, baharatlar", en: "Fresh potatoes, salt, spices" }, image: "/images/food/patates_sepeti.png" },
      { name: { tr: "Sandviç", en: "Sandwich" }, price: "200 TL", description: { tr: "Taze malzemelerle hazırlanan lezzetli sandviç.", en: "Delicious sandwich with fresh ingredients." }, ingredients: { tr: "Taze ekmek, peynir, domates, marul, salatalık", en: "Fresh bread, cheese, tomato, lettuce, cucumber" }, image: "/images/food/sandvic.png" },
      { name: { tr: "Sosis Sepeti", en: "Sausage Basket" }, price: "280 TL", description: { tr: "Kızarmış sosis ve patates cipsleri bir arada.", en: "Fried sausages and potato chips together." }, ingredients: { tr: "Sosis, patates, özel baharatlar", en: "Sausage, potatoes, special spices" }, image: "/images/food/sosis_sepeti.png" },
      { name: { tr: "SOO HO Sepeti", en: "SOO HO Basket" }, price: "400 TL", badge: { tr: "⭐ En Çok Satan", en: "⭐ Best Seller" }, description: { tr: "Cips, nugget, soğan halkası, sosis ve mozzarella stick.", en: "Premium basket with chips, nuggets, onion rings, sausages and mozzarella sticks." }, ingredients: { tr: "Patates cipsi, nugget, soğan halkası, sosis, mozzarella stick", en: "Potato chips, nuggets, onion rings, sausage, mozzarella stick" }, image: "/images/food/sooho_sepeti.png" },
    ]
  },
  /* ── ANA YEMEK ── */
  {
    id: 'ana-yemekler',
    label: { tr: 'Ana Yemek', en: 'Main Course' },
    emoji: '🍽️',
    color: '#7c3aed',
    items: [
      { name: { tr: "Körili Tavuk", en: "Curry Chicken" }, price: "400 TL", description: { tr: "Körili tavuk, kremalı makarna ve çips eşliğinde.", en: "Curry chicken served with creamy pasta and chips." }, ingredients: { tr: "Tavuk göğsü, köri sosu, krema, makarna, patates cipsi", en: "Chicken breast, curry sauce, cream, pasta, potato chips" }, image: "/images/food/korili_tavuk.png" },
      { name: { tr: "Kekikli Tavuk", en: "Thyme Chicken" }, price: "400 TL", description: { tr: "Kekik ile harmanlanan tavuk, kremalı makarna.", en: "Thyme chicken with creamy pasta and chips." }, ingredients: { tr: "Tavuk göğsü, kekik, krema, makarna, patates cipsi", en: "Chicken breast, thyme, cream, pasta, potato chips" }, image: "/images/food/kekikli_tavuk.png" },
      { name: { tr: "Barbekü Tavuk", en: "BBQ Chicken" }, price: "400 TL", description: { tr: "Barbekü sosla marine edilmiş tavuk, kremalı makarna.", en: "BBQ marinated chicken with creamy pasta." }, ingredients: { tr: "Tavuk göğsü, barbekü sos, krema, makarna, patates cipsi", en: "Chicken breast, BBQ sauce, cream, pasta, potato chips" }, image: "/images/food/barbeku_tavuk.png" },
      { name: { tr: "Penne Carbonara", en: "Penne Carbonara" }, price: "350 TL", description: { tr: "Penne makarna, mantar, salam ve krema.", en: "Penne pasta with mushrooms, salami and cream." }, ingredients: { tr: "Penne makarna, mantar, salam, krema", en: "Penne pasta, mushroom, salami, cream" }, image: "/images/food/penne_carbonara.png" },
      { name: { tr: "Spagetti Bolonez", en: "Spaghetti Bolognese" }, price: "350 TL", description: { tr: "Spagetti makarna, kıyma ve domates sosu.", en: "Spaghetti with ground beef and tomato sauce." }, ingredients: { tr: "Spagetti, kıyma, domates sosu", en: "Spaghetti, ground beef, tomato sauce" }, image: "/images/food/spagetti_bolonez.png" },
      { name: { tr: "Porsiyon Köfte", en: "Meatball Plate" }, price: "500 TL", description: { tr: "Köfte, közlenmiş biber, domates ve çips.", en: "Meatballs with roasted peppers, tomatoes and chips." }, ingredients: { tr: "Dana köfte, közlenmiş biber, domates, patates cipsi", en: "Beef meatballs, roasted pepper, tomato, potato chips" }, image: "/images/food/porsiyon_kofte.png" },
    ]
  },
  /* ── TATLILAR ── */
  {
    id: 'tatlilar',
    label: { tr: 'Tatlılar', en: 'Desserts' },
    emoji: '🍰',
    color: '#db2777',
    items: [
      { name: { tr: "Waffle", en: "Waffle" }, price: "380 TL", description: { tr: "Çıtır waffle, seçtiğiniz sos (çikolata / karamel / meyveli) ve taze meyvelerle servis edilir.", en: "Crispy waffle with sauce (chocolate / caramel / fruit) and fresh fruits." }, ingredients: { tr: "Waffle hamuru, seçtiğiniz sos, taze meyveler", en: "Waffle batter, sauce of your choice, fresh fruits" }, image: "/images/food/waffle.png" },
      { name: { tr: "Cookie", en: "Cookie" }, price: "150 TL", description: { tr: "Taze pişmiş, çikolata parçacıklı cookie.", en: "Freshly baked chocolate chip cookie." }, ingredients: { tr: "Un, çikolata parçacıkları, tereyağı, şeker", en: "Flour, chocolate chips, butter, sugar" }, image: "/images/food/cookie.png" },
      { name: { tr: "Tatlı Çeşitleri", en: "Dessert Varieties" }, price: "300 TL", description: { tr: "Günlük taze tatlı çeşitlerimiz. Garsonunuza sorunuz.", en: "Daily fresh desserts. Please ask your waiter." }, ingredients: { tr: "Günlük tatlılar", en: "Daily desserts" }, image: "/images/food/tatli_cesitleri.png" },
      { name: { tr: "Marshmallow", en: "Marshmallow" }, price: "100 TL", description: { tr: "Çubukta 3 adet marshmallow, keyifli atıştırmalık.", en: "3 marshmallows on a stick, a fun snack." }, ingredients: { tr: "Marshmallow (3 adet)", en: "Marshmallow (3 pieces)" }, image: "/images/drinks/marshmallow.png" },
    ]
  }
];
/* ─────────────────────────────────────────────
   LIVE EVENTS
───────────────────────────────────────────── */
const liveEvents = [
  // Etkinlik eklemek için aşağıdaki örneği kopyalayın:
  // {
  //   id: "ev-001",
  //   day: { tr: "Cuma", en: "Friday" },
  //   date: "20 Haziran",
  //   time: "21:30",
  //   artist: "Sanatçı Adı",
  //   genre: { tr: "Akustik", en: "Acoustic" },
  //   note: { tr: "Rezervasyon önerilir", en: "Reservation recommended" },
  //   bio: { tr: "Kısa biyografi...", en: "Short bio..." },
  //   coverImage: "/images/events/kapak.jpg",
  //   photos: ["/images/events/f1.jpg"],
  //   videoUrl: "https://www.youtube.com/embed/VIDEO_ID",
  //   spotifyUrl: "https://open.spotify.com/artist/...",
  //   songs: ["Şarkı 1", "Şarkı 2"],
  //   instagramHandle: "@sanatci",
  // }
];

const TICKET_PRICE = "200 TL";

/* ─────────────────────────────────────────────
   SLIDES DATA (outside App to avoid re-render)
───────────────────────────────────────────── */
const slides = [
  {
    image: '/images/slider_coffee.jpg',
    category: { tr: '☕ Sıcak Kahve', en: '☕ Hot Coffee' },
    title: { tr: 'Mükemmel Bir Yudum', en: 'The Perfect Sip' },
    subtitle: { tr: 'El yapımı latte sanatı ve özel kavurma kahvelerimizle güne merhaba deyin.', en: 'Say hello to the day with handcrafted latte art and specialty roast coffees.' },
    cta: { tr: 'Kahveleri Keşfet', en: 'Explore Coffees' },
    categoryId: 'sicak-kahve',
    accent: '#92400e',
  },
  {
    image: '/images/slider_burger.jpg',
    category: { tr: '🍔 Burgerler', en: '🍔 Burgers' },
    title: { tr: 'Efsane Lezzet', en: 'Legendary Taste' },
    subtitle: { tr: '180 gr dana köfte, eriyen cheddar ve özel sosumuzla hazırlanan burgerleri denediniz mi?', en: 'Have you tried our 180g beef patty burgers with melting cheddar and special sauce?' },
    cta: { tr: 'Burgerleri Gör', en: 'See Burgers' },
    categoryId: 'burgerler',
    accent: '#dc2626',
  },
  {
    image: '/images/slider_desserts.jpg',
    category: { tr: '🍰 Tatlılar', en: '🍰 Desserts' },
    title: { tr: 'Tatlı Bir Kaçış', en: 'A Sweet Escape' },
    subtitle: { tr: 'Taze pişmiş cookieler, cheesecake ve günlük tatlı çeşitlerimizle kendinizi şımartın.', en: 'Treat yourself to freshly baked cookies, cheesecake and our daily dessert selection.' },
    cta: { tr: 'Tatlılara Bak', en: 'View Desserts' },
    categoryId: 'tatlilar',
    accent: '#db2777',
  },
  {
    image: '/images/slider_drinks.jpg',
    category: { tr: '🧐 Soğuk İçecekler', en: '🧐 Cold Drinks' },
    title: { tr: 'Serinle & Canlan', en: 'Chill & Refresh' },
    subtitle: { tr: 'Meyve suları, enerji içecekleri ve serinletici seçeneklerimizle yazın tadını çıkarın.', en: 'Enjoy summer with fruit juices, energy drinks and refreshing options.' },
    cta: { tr: 'İçecekleri Keşfet', en: 'Explore Drinks' },
    categoryId: 'soguk-icecek',
    accent: '#0284c7',
  },
  {
    image: '/images/slider_pasta.jpg',
    category: { tr: '🍽️ Ana Yemek', en: '🍽️ Main Course' },
    title: { tr: 'Doyurucu Lezzet', en: 'Hearty Flavour' },
    subtitle: { tr: 'Kremali carbonara, körili tavuk ve daha fazlası — her damak zevkine özel ana yemekler.', en: 'Creamy carbonara, curry chicken and more — main courses for every palate.' },
    cta: { tr: 'Ana Yemekler', en: 'Main Courses' },
    categoryId: 'ana-yemekler',
    accent: '#7c3aed',
  },
];

/* ─────────────────────────────────────────────
   HERO SLIDER (standalone component — isolated state,
   so auto-advance never re-renders App)
───────────────────────────────────────────── */
function HeroSlider({ lang, selectCategory }) {
  const [idx, setIdx] = React.useState(0);
  const slide = slides[idx];

  React.useEffect(() => {
    // Auto-advance disabled to prevent mobile stutter
    // const timer = setInterval(() => setIdx(i => (i + 1) % slides.length), 4000);
    // return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full overflow-hidden mb-6" style={{ borderRadius: 20, height: 220 }}>
      {slides.map((s, i) => (
        <div key={i} className="absolute inset-0"
          style={{
            opacity: i === idx ? 1 : 0,
            transform: i === idx ? 'scale(1)' : 'scale(1.04)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}>
          <img src={s.image} alt={s.title[lang]} className="w-full h-full object-cover" />
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.08) 100%)' }} />
        </div>
      ))}
      <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-8" style={{ zIndex: 10 }}>
        <span className="inline-flex items-center self-start text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 text-white"
          style={{ background: `${slide.accent}cc`, backdropFilter: 'blur(4px)' }}>
          {slide.category[lang]}
        </span>
        <h2 className="font-jakarta font-black text-white leading-tight mb-2"
          style={{ fontSize: 'clamp(1.4rem, 4vw, 2rem)', textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
          {slide.title[lang]}
        </h2>
        <p className="text-white/80 text-xs leading-relaxed mb-4 max-w-xs"
          style={{ textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>
          {slide.subtitle[lang]}
        </p>
        <button
          onClick={() => selectCategory(slide.categoryId)}
          className="self-start flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95"
          style={{ background: slide.accent, boxShadow: `0 4px 16px ${slide.accent}60` }}>
          {slide.cta[lang]}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="absolute bottom-3 right-4 flex gap-1.5" style={{ zIndex: 10 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className="transition-all duration-300 rounded-full"
            style={{ width: i === idx ? 20 : 6, height: 6, background: i === idx ? '#ffffff' : 'rgba(255,255,255,0.4)' }} />
        ))}
      </div>
      <button onClick={() => setIdx(i => (i - 1 + slides.length) % slides.length)}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ zIndex: 10, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' }}>
        <ChevronLeft className="w-4 h-4 text-white" />
      </button>
      <button onClick={() => setIdx(i => (i + 1) % slides.length)}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ zIndex: 10, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' }}>
        <ChevronRight className="w-4 h-4 text-white" />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
function App() {
  const [activeTab, setActiveTab] = useState('menu');
  const [activeCategoryId, setActiveCategoryId] = useState('cay-sicak');
  const [lang, setLang] = useState('tr');
  const [activeEventIdx, setActiveEventIdx] = useState(0);

  const pillBarRef = useRef(null);
  const pillRefs = useRef({});



  const t = translations[lang];
  const activeEvent = liveEvents[activeEventIdx] || null;
  const activeCategory = menuCategories.find(c => c.id === activeCategoryId) || menuCategories[0];

  // Preload all menu images to prevent pop-in delay on category switch
  useEffect(() => {
    menuCategories.forEach(cat => {
      cat.items.forEach(item => {
        if (item.image) {
          const img = new Image();
          img.src = item.image;
        }
      });
    });
  }, []);

  // Scroll active pill into view — only moves the pill bar, not the page
  useEffect(() => {
    const el = pillRefs.current[activeCategoryId];
    const bar = pillBarRef.current;
    if (el && bar) {
      const elLeft = el.offsetLeft;
      const elWidth = el.offsetWidth;
      const barWidth = bar.offsetWidth;
      const scrollTarget = elLeft - barWidth / 2 + elWidth / 2;
      bar.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }
  }, [activeCategoryId]);

  const selectCategory = (id) => {
    setActiveCategoryId(id);
  };




  /* ── Language Switcher ── */
  const languageSwitcherJSX = (isDark) => (
    <div className={`flex gap-1 p-1 rounded-full border ${isDark ? 'border-white/15 bg-white/5' : 'border-teal-600/20 bg-white/70 backdrop-blur-sm'}`}>
      {['tr', 'en'].map(l => (
        <button key={l} onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${lang === l
            ? (isDark ? 'bg-orange-500 text-white' : 'bg-teal-600 text-white')
            : (isDark ? 'text-white/40 hover:text-white/70' : 'text-teal-600/50 hover:text-teal-600')}`}>
          {l}
        </button>
      ))}
    </div>
  );

  /* ──────────────────────────────────
     MENU ITEM CARD — Starbucks style horizontal (mobile + desktop)
  ────────────────────────────────── */
  const menuItemJSX = (item, accentColor, idx) => (
    <div key={idx} className="group flex items-center gap-4 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden p-3 lg:p-4">
      {/* Square image — left */}
      <div className="flex-shrink-0 w-20 h-20 lg:w-24 lg:h-24 rounded-xl overflow-hidden relative"
        style={{ background: `linear-gradient(135deg, ${accentColor}18, ${accentColor}06)` }}>
        {item.image && (
          <img src={item.image} alt={item.name[lang]}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        )}
        <div className="absolute inset-0 flex items-center justify-center text-3xl"
          style={{ display: item.image ? 'none' : 'flex' }}>
          {activeCategory.emoji}
        </div>
        {/* Color accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
          style={{ background: accentColor }} />
      </div>

      {/* Content — right */}
      <div className="flex-1 min-w-0">
        {item.badge && (
          <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md mb-1.5"
            style={{ background: `${accentColor}15`, color: accentColor }}>
            {item.badge[lang]}
          </span>
        )}
        <h3 className="font-jakarta text-sm font-bold text-gray-800 leading-snug mb-1 group-hover:text-gray-900 transition-colors">
          {item.name[lang]}
        </h3>
        <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-2 mb-2">
          {item.description[lang]}
        </p>
        <p className="text-[10px] text-gray-300 line-clamp-1">
          <span className="text-gray-400 font-medium">{t.ingredients} </span>
          {item.ingredients[lang]}
        </p>
      </div>

      {/* Price — right edge */}
      <div className="flex-shrink-0 flex flex-col items-end justify-between self-stretch py-0.5">
        <span className="text-xs font-black text-white px-2.5 py-1 rounded-lg shadow-sm"
          style={{ background: accentColor }}>
          {item.price}
        </span>
        <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-gray-400 group-hover:translate-x-0.5 transition-all" />
      </div>
    </div>
  );

  /* ──────────────────────────────────
     ENTERTAINMENT — TICKET
  ────────────────────────────────── */
  const TicketCard = () => (
    <div className="relative w-full mb-8">
      {/* Glow bg */}
      <div className="absolute inset-0 rounded-3xl blur-xl opacity-30"
        style={{ background: 'linear-gradient(135deg, #FF6B4A, #F59E0B)' }} />
      <div className="relative rounded-3xl overflow-hidden border border-white/10"
        style={{ background: 'linear-gradient(135deg, #1c1009 0%, #0f0a04 100%)' }}>
        {/* Top accent line */}
        <div className="h-[3px] w-full"
          style={{ background: 'linear-gradient(90deg, #FF6B4A, #F59E0B, #FF6B4A)' }} />
        <div className="flex items-stretch">
          {/* Left: ticket info */}
          <div className="flex-1 px-7 py-6">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/30 block mb-3">🎫 {t.ticket}</span>
            <p className="font-jakarta font-black text-2xl text-white leading-tight mb-1">
              {lang === 'tr' ? 'Canlı Müzik' : 'Live Music'}
            </p>
            <p className="font-jakarta font-black text-2xl leading-tight mb-4"
              style={{ background: 'linear-gradient(90deg, #FF6B4A, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {lang === 'tr' ? 'Katılım Bileti' : 'Entry Ticket'}
            </p>
            <div className="flex items-center gap-2 text-white/30 text-[10px] uppercase tracking-widest">
              <Music className="w-3 h-3" />
              SOO HO • Summer 2026
            </div>
          </div>
          {/* Tear line */}
          <div className="flex flex-col items-center justify-between py-4 relative">
            <div className="w-4 h-4 rounded-full -translate-x-1/2" style={{ background: '#0A0E1A' }} />
            <div className="w-px flex-1 border-l-2 border-dashed border-white/10" />
            <div className="w-4 h-4 rounded-full -translate-x-1/2" style={{ background: '#0A0E1A' }} />
          </div>
          {/* Right: price */}
          <div className="flex flex-col items-center justify-center px-7 py-6">
            <span className="text-[10px] uppercase tracking-widest text-white/25 mb-2">{t.ticketPrice}</span>
            <div className="font-jakarta font-black text-4xl"
              style={{ background: 'linear-gradient(135deg, #FF6B4A, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {TICKET_PRICE}
            </div>
            <span className="text-white/25 text-[10px] mt-2">{lang === 'tr' ? 'kişi başı' : 'per person'}</span>
          </div>
        </div>
        {/* Bottom accent line */}
        <div className="h-[3px] w-full"
          style={{ background: 'linear-gradient(90deg, #FF6B4A, #F59E0B, #FF6B4A)' }} />
      </div>
    </div>
  );

  /* ──────────────────────────────────
     ENTERTAINMENT — EVENT HERO
  ────────────────────────────────── */
  const EventHeroCard = ({ event }) => (
    <div className="relative rounded-3xl overflow-hidden mb-6 min-h-[260px]"
      style={{ background: 'linear-gradient(160deg, #0f0515 0%, #0a1628 60%, #0d0a1e 100%)' }}>
      {/* Cover image */}
      {event.coverImage && (
        <div className="absolute inset-0">
          <img src={event.coverImage} alt={event.artist} className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, rgba(10,5,21,0.9) 0%, rgba(10,5,21,0.5) 100%)' }} />
        </div>
      )}
      {/* Ambient glow — no cover */}
      {!event.coverImage && (
        <>
          <div className="absolute top-0 left-0 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,107,74,0.18) 0%, transparent 70%)', transform: 'translate(-30%, -30%)' }} />
          <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)', transform: 'translate(30%, 30%)' }} />
          {/* Vinyl record decoration */}
          <div className="absolute right-8 top-1/2 -translate-y-1/2 w-28 h-28 rounded-full opacity-15 flex items-center justify-center"
            style={{ background: 'conic-gradient(#111, #333, #111, #333, #111)', border: '2px solid rgba(255,255,255,0.1)' }}>
            <div className="w-8 h-8 rounded-full bg-gray-900 border border-white/10" />
          </div>
        </>
      )}

      <div className="relative z-10 p-6 lg:p-8">
        {/* Top badges row */}
        <div className="flex items-center gap-2 mb-5">
          <span className="text-[9px] uppercase tracking-[0.35em] font-bold px-3 py-1 rounded-full border"
            style={{ color: '#FF6B4A', borderColor: 'rgba(255,107,74,0.4)', background: 'rgba(255,107,74,0.12)' }}>
            🎵 {event.genre?.[lang] || 'Live'}
          </span>
          {event.instagramHandle && (
            <a href={`https://instagram.com/${event.instagramHandle.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[10px] text-white/35 hover:text-white/60 transition-colors">
              <Instagram className="w-3 h-3" />{event.instagramHandle}
            </a>
          )}
        </div>

        {/* Artist name — big */}
        <h3 className="font-jakarta font-black text-white leading-none mb-4"
          style={{ fontSize: 'clamp(2rem, 6vw, 3.5rem)' }}>
          {event.artist}
        </h3>

        {/* Info row */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5">
          {(event.day || event.date) && (
            <div className="flex items-center gap-1.5 font-medium" style={{ color: '#F59E0B' }}>
              <CalendarDays className="w-4 h-4" />
              <span className="text-sm">{event.day?.[lang]} {event.date}</span>
            </div>
          )}
          {event.time && (
            <div className="flex items-center gap-1.5 text-white/45 text-sm">
              <Clock className="w-4 h-4" /><span>{event.time}</span>
            </div>
          )}
          {event.note && (
            <div className="flex items-center gap-1.5 text-white/35 text-xs">
              <Info className="w-3 h-3" /><span>{event.note[lang]}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        {event.bio && (
          <p className="text-white/55 text-sm leading-relaxed max-w-lg mb-5">{event.bio[lang]}</p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          {event.spotifyUrl && (
            <a href={event.spotifyUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white hover:scale-105 transition-transform"
              style={{ background: '#1DB954' }}>
              <Music className="w-3.5 h-3.5" />{t.listenSpotify}
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          )}
        </div>
      </div>
    </div>
  );

  /* ──────────────────────────────────
     ENTERTAINMENT — SETLIST
  ────────────────────────────────── */
  const Setlist = ({ songs }) => (
    <div className="mb-7">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/30 mb-3 flex items-center gap-2">
        <ListMusic className="w-3.5 h-3.5 text-orange-400" />{t.setlist}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {songs.map((song, i) => (
          <div key={i}
            className="flex items-center gap-3 bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.06] rounded-xl px-4 py-3 transition-colors group">
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
              style={{ background: 'rgba(255,107,74,0.2)', color: '#FF6B4A' }}>{i + 1}</span>
            <span className="text-white/65 text-sm group-hover:text-white/85 transition-colors truncate">{song}</span>
          </div>
        ))}
      </div>
    </div>
  );

  /* ──────────────────────────────────
     ENTERTAINMENT — GALLERY
  ────────────────────────────────── */
  const Gallery = ({ photos }) => (
    <div className="mb-7">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/30 mb-3 flex items-center gap-2">
        <Camera className="w-3.5 h-3.5 text-orange-400" />{t.gallery}
      </p>
      <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
        {photos.map((photo, i) => (
          <div key={i}
            className="flex-shrink-0 rounded-2xl overflow-hidden border border-white/10 hover:border-white/25 transition-colors"
            style={{ width: 160, height: 110 }}>
            <img src={photo} alt={`Etkinlik ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
          </div>
        ))}
      </div>
    </div>
  );

  /* ──────────────────────────────────
     ENTERTAINMENT — MAIN CONTENT
  ────────────────────────────────── */
  const entertainmentContentJSX = (
    <div className="min-h-screen" style={{ background: '#080810' }}>
      {/* Full-width teal-to-dark header band */}
      <div className="relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #3D8E8A 0%, #065f4a 40%, #080810 100%)', paddingTop: 1 }}>
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #FF6B4A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #F59E0B 0%, transparent 50%)' }} />
        {/* Animated EQ bars across top */}
        <div className="relative flex items-end justify-center gap-[3px] pt-10 pb-0" style={{ height: 80 }}>
          {Array.from({ length: 32 }).map((_, i) => {
            const heights = [18,30,42,24,36,48,22,38,50,28,44,20,34,46,26,40,52,24,36,18,42,28,38,50,22,44,30,48,20,36,26,42];
            return (
              <div key={i}
                className="rounded-t-sm animate-eq-bar"
                style={{
                  width: 5,
                  height: heights[i],
                  background: i % 3 === 0 ? '#FF6B4A' : i % 3 === 1 ? '#F59E0B' : 'rgba(255,255,255,0.4)',
                  animationDelay: `${i * 0.06}s`,
                  animationDuration: `${0.6 + (i % 4) * 0.15}s`,
                  opacity: 0.7,
                }}
              />
            );
          })}
        </div>
        {/* Section title */}
        <div className="relative text-center px-6 pt-8 pb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-white/30" />
            <Music className="w-4 h-4 text-white/60" />
            <div className="h-px w-8 bg-white/30" />
          </div>
          <h2 className="font-jakarta text-4xl lg:text-5xl font-black text-white mb-2 tracking-tight">
            {t.entertainmentTab}
          </h2>
          <p className="text-white/50 text-sm">
            {lang === 'tr' ? 'Müziğin ritmiyle yazın tadını çıkarın' : 'Enjoy summer to the rhythm of music'}
          </p>
        </div>
      </div>

      {/* Content area */}
      <div className="px-4 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Event navigator */}
          {liveEvents.length > 1 && (
            <div className="flex items-center justify-center gap-4 mb-6">
              <button onClick={() => setActiveEventIdx(i => (i - 1 + liveEvents.length) % liveEvents.length)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 hover:bg-white/15 text-white/60 hover:text-white text-sm transition-all">
                <ChevronLeft className="w-4 h-4" />{lang === 'tr' ? 'Önceki' : 'Prev'}
              </button>
              <span className="text-white/30 text-xs tracking-wider">{activeEventIdx + 1} / {liveEvents.length}</span>
              <button onClick={() => setActiveEventIdx(i => (i + 1) % liveEvents.length)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 hover:bg-white/15 text-white/60 hover:text-white text-sm transition-all">
                {lang === 'tr' ? 'Sonraki' : 'Next'}<ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {activeEvent ? (
            <div>
              <EventHeroCard event={activeEvent} />
              {activeEvent.songs?.length > 0 && <Setlist songs={activeEvent.songs} />}
              {activeEvent.photos?.length > 0 && <Gallery photos={activeEvent.photos} />}
              {activeEvent.videoUrl && (
                <div className="mb-7">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/30 mb-3 flex items-center gap-2">
                    <Youtube className="w-3.5 h-3.5 text-orange-400" />{t.watchVideo}
                  </p>
                  <div className="relative w-full rounded-2xl overflow-hidden border border-white/10" style={{ paddingBottom: '56.25%' }}>
                    <iframe src={activeEvent.videoUrl} className="absolute inset-0 w-full h-full" title="Event Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                </div>
              )}
              <TicketCard />
            </div>
          ) : (
            /* Empty state — elegant */
            <div className="flex flex-col items-center text-center py-8">
              {/* Vinyl record */}
              <div className="relative w-40 h-40 mb-8">
                <div className="absolute inset-0 rounded-full animate-spin-slow"
                  style={{ background: 'conic-gradient(#1a1a1a 0deg, #2a2a2a 90deg, #1a1a1a 180deg, #2a2a2a 270deg, #1a1a1a 360deg)', border: '2px solid rgba(255,255,255,0.08)' }} />
                {/* Groove rings */}
                {[70, 80, 90, 100, 110, 120, 130].map(r => (
                  <div key={r} className="absolute rounded-full border border-white/[0.03]"
                    style={{ inset: `${(140-r)/2}px` }} />
                ))}
                {/* Label */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center border border-white/10"
                    style={{ background: 'linear-gradient(135deg, #FF6B4A22, #F59E0B22)' }}>
                    <Music className="w-6 h-6 text-orange-400" />
                  </div>
                </div>
              </div>
              <h3 className="font-jakarta text-2xl font-black text-white mb-3">{t.noEvent}</h3>
              <p className="text-white/35 text-sm leading-relaxed max-w-xs mb-10">{t.noEventSub}</p>
              <div className="w-full max-w-md"><TicketCard /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ──────────────────────────────────
     SHARED HEADER
  ────────────────────────────────── */
  const headerJSX = (
    <div className="sticky top-0 z-50" style={{ background: '#4A9E9B' }}>
      <div className="px-4 lg:px-8 pt-3 pb-0">
        <div className="flex items-center justify-between pb-3">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center border border-white/20 bg-white/10">
              <Sun className="w-5 h-5 text-yellow-300" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-jakarta text-2xl font-black text-white leading-none tracking-tight">
                SOO <span className="text-yellow-300">HO</span>
              </h1>
              <p className="text-white/40 text-[9px] tracking-widest uppercase mt-0.5">{t.digitalMenu}</p>
            </div>
          </div>

          {/* Desktop: main tabs inside header */}
          <div className="hidden lg:flex items-center gap-1 border border-white/20 rounded-full p-1 bg-white/5">
            {[
              { key: 'menu', label: t.menuTab, icon: UtensilsCrossed },
              { key: 'entertainment', label: t.entertainmentTab, icon: Music },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} id={`tab-${key}`} onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold font-jakarta transition-all duration-200 ${activeTab === key
                  ? 'bg-yellow-300 text-teal-900'
                  : 'text-white/50 hover:text-white/80'}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>

          {languageSwitcherJSX(true)}
        </div>

        {/* Mobile: tabs + pill bar */}
        <div className="lg:hidden">
          <div className="flex border-t border-white/10">
            {[
              { key: 'menu', label: t.menuTab, icon: UtensilsCrossed },
              { key: 'entertainment', label: t.entertainmentTab, icon: Music },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} id={`tab-mob-${key}`} onClick={() => setActiveTab(key)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold font-jakarta transition-all border-b-2 ${activeTab === key
                  ? 'border-yellow-300 text-yellow-300'
                  : 'border-transparent text-white/40'}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>

          {/* Mobile category pill bar */}
          {activeTab === 'menu' && (
            <div ref={pillBarRef} className="flex gap-2 py-2.5 border-t border-white/[0.06] overflow-x-auto scrollbar-hide">
              {menuCategories.map(cat => (
                <button key={cat.id} id={`pill-${cat.id}`}
                  ref={el => pillRefs.current[cat.id] = el}
                  onClick={() => selectCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold font-jakarta whitespace-nowrap flex-shrink-0 transition-all duration-150 ${activeCategoryId === cat.id ? 'text-white scale-105' : 'bg-white/10 text-white/55 hover:bg-white/18'}`}
                  style={activeCategoryId === cat.id ? { background: cat.color } : {}}>
                  <span className="text-sm leading-none">{cat.emoji}</span>{cat.label[lang]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ──────────────────────────────────
     DESKTOP SIDEBAR
  ────────────────────────────────── */
  const desktopSidebarJSX = (
    <aside className="hidden lg:flex flex-col w-60 xl:w-64 flex-shrink-0">
      <div className="sticky top-[72px] pt-6 pb-10 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 72px)' }}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-3">
          {lang === 'tr' ? 'Kategoriler' : 'Categories'}
        </p>
        <nav className="flex flex-col gap-1">
          {menuCategories.map(cat => (
            <button key={cat.id}
              onClick={() => selectCategory(cat.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold font-jakarta text-left transition-all duration-150 group ${activeCategoryId === cat.id ? 'text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
              style={activeCategoryId === cat.id ? { background: `linear-gradient(135deg, ${cat.color}, ${cat.color}cc)` } : {}}>
              <span className="text-base leading-none w-6 text-center flex-shrink-0">{cat.emoji}</span>
              <span className="flex-1 truncate">{cat.label[lang]}</span>
              <span className={`text-[10px] font-medium flex-shrink-0 transition-colors ${activeCategoryId === cat.id ? 'text-white/60' : 'text-gray-400'}`}>
                {cat.items.length}
              </span>
            </button>
          ))}
        </nav>

        {/* Contact card in sidebar */}
        <div className="mt-8 mx-3 p-4 rounded-2xl border border-gray-100 bg-gray-50">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">
            {t.contact}
          </p>
          {[
            ['https://wa.me/905012507646', '_blank', Phone, '+90 501 250 76 46'],
            ['mailto:sooholcelounge@gmail.com', undefined, Mail, 'sooholcelounge@gmail.com'],
            ['https://instagram.com/soohoicelounge', '_blank', Instagram, '@soohoicelounge'],
          ].map(([href, target, Icon, label], i) => (
            <a key={i} href={href} target={target} rel={target ? 'noopener noreferrer' : undefined}
              className="flex items-center gap-2 py-2 text-xs text-gray-500 hover:text-teal-600 transition-colors group">
              <Icon className="w-3.5 h-3.5 text-gray-400 group-hover:text-teal-600 flex-shrink-0 transition-colors" />
              <span className="truncate">{label}</span>
            </a>
          ))}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-gray-400 leading-relaxed">Dedeman Ski Lodge<br />Üst Bahçesi / 25080</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );

  /* ──────────────────────────────────
     INLINE RENDER HELPERS
  ────────────────────────────────── */
  const menuContentJSX = (
    <div className="flex-1 min-w-0">
      <HeroSlider lang={lang} selectCategory={selectCategory} />
      <div className="mb-6 px-1">
        <div className="flex items-center gap-3 py-4 border-b border-gray-100">
          <span className="text-3xl">{activeCategory.emoji}</span>
          <div>
            <h2 className="font-jakarta text-xl font-black text-gray-800">{activeCategory.label[lang]}</h2>
            <p className="text-xs text-gray-400">{activeCategory.items.length} {t.items}</p>
          </div>
          <div className="ml-auto w-2 h-10 rounded-full" style={{ background: `linear-gradient(to bottom, ${activeCategory.color}, ${activeCategory.color}50)` }} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 lg:gap-3">
        {activeCategory.items.map((item, idx) => (
          menuItemJSX(item, activeCategory.color, idx)
        ))}
      </div>
      <div className="mt-10 pt-6 border-t border-gray-100">
        <p className="font-jakarta text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">{t.otherCategories}</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {menuCategories.filter(c => c.id !== activeCategoryId).slice(0, 6).map(cat => (
            <button key={cat.id} onClick={() => selectCategory(cat.id)}
              className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all text-left group">
              <span className="text-xl">{cat.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-jakarta text-xs font-bold text-gray-700 group-hover:text-gray-900 truncate">{cat.label[lang]}</p>
                <p className="text-[10px] text-gray-400">{cat.items.length} {t.items}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  /* ──────────────────────────────────
     FOOTER (desktop only, shown below content)
  ────────────────────────────────── */
  const footerJSX = (
    <footer style={{ background: '#0A0E1A' }} className="text-white py-10 mt-0">
      <div className="px-4 lg:px-8">
        <div className="h-px mb-8" style={{ background: 'linear-gradient(90deg, transparent, #FF6B4A40, transparent)' }} />
        <div className="max-w-4xl flex flex-col lg:flex-row gap-6 items-start">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-jakarta font-bold text-amber-400 text-sm mb-1">{t.location}</p>
              <p className="text-white/45 text-sm leading-relaxed">Dedeman Ski Lodge<br />Üst Bahçesi / 25080</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 flex-1">
            {[
              ['https://wa.me/905012507646', '_blank', Phone, '+90 501 250 76 46'],
              ['mailto:sooholcelounge@gmail.com', undefined, Mail, 'sooholcelounge@gmail.com'],
              ['https://instagram.com/soohoicelounge', '_blank', Instagram, '@soohoicelounge'],
            ].map(([href, target, Icon, label], i) => (
              <a key={i} href={href} target={target} rel={target ? 'noopener noreferrer' : undefined}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 hover:border-orange-500/30 bg-white/3 hover:bg-white/6 transition-all text-sm text-white/50 group">
                <Icon className="w-4 h-4 text-orange-400 group-hover:scale-110 transition-transform" />
                {label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-8 pt-5 border-t border-white/8 text-center">
          <p className="text-orange-400/60 text-sm mb-1">{t.enjoy}</p>
          <p className="text-white/20 text-xs">© 2026 SOO HO • Summer Edition</p>
        </div>
      </div>
    </footer>
  );

  /* ──────────────────────────────────
     RENDER
  ────────────────────────────────── */
  return (
    <div className="font-inter antialiased min-h-screen" style={{ background: '#FFF8F0' }}>
      {headerJSX}

      {activeTab === 'menu' && (
        <div className="flex min-h-screen">
          {/* Desktop Sidebar */}
          {desktopSidebarJSX}

          {/* Main content — inline JSX to prevent remount on state change */}
          <main className="flex-1 px-4 lg:px-8 pt-6 pb-16">
            {menuContentJSX}
          </main>
        </div>
      )}

      {activeTab === 'entertainment' && (
        entertainmentContentJSX
      )}

      {footerJSX}
    </div>
  );
}

export default App;
